import {
  Autocomplete,
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Address, AddressRequest, ResponseObject } from "../../classes/main";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "../../services/HolderService";
import { useLoadScript } from "@react-google-maps/api";
import type { Libraries } from "@googlemaps/js-api-loader";

interface AutocompletePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
  types: string[];
}

const API_KEY = "AIzaSyCY519Z3pdrcYUmVhOV8NftANVlYYoVRKQ";
const BRAND = "#1f7a4d";

const Input = styled(TextField)(({ theme }) => ({
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
}));

const AutocompleteListPaper = styled(Paper)(({ theme }) => ({
  "&::-webkit-scrollbar": { display: "none" },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  width: "100%",
  maxWidth: "100%",
  marginTop: theme.spacing(1),
  maxHeight: "min(40vh, 280px)",
  overflowY: "auto",
}));

interface AddressInputDialogProps {
  addressOpen: any;
  handleClose: any;
  addressForSave: any;
  updateAddressList: any;
  updateAddressFlag: any;
}

export const AddressInputDialog: React.FC<AddressInputDialogProps> = ({
  addressOpen,
  handleClose,
  addressForSave,
  updateAddressList,
  updateAddressFlag,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [address, setAddress] = React.useState<Address | undefined>(undefined);
  const [addressLimitFlag, setAddressLimitFlag] = useState(false);
  const [addressLimitMessage, setAddressLimitMessage] = useState<
    string | undefined
  >(undefined);
  const [clicked, setClicked] = React.useState(false);
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [inputValue, setInputValue] = useState("");
  const apiService: ApiService = useMemo(() => new ApiService(), []);
  const libraries = useRef<Libraries>(["places"]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries: libraries.current,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  React.useEffect(() => {
    if (addressForSave) {
      setAddress({
        building: addressForSave.building || "",
        locality: addressForSave.locality || "",
        landmark: addressForSave.formattedAddress || "",
        placeId: addressForSave.placeId || "",
        latitude: addressForSave.latitude || "",
        longitude: addressForSave.longitude || "",
      });
    }
  }, [addressForSave]);

  const handleAddress = (e: any) => {
    setAddress((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e: any) => {
    if (
      address?.building == null ||
      address?.building == undefined ||
      address?.building.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.locality == null ||
      address?.locality == undefined ||
      address?.locality.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.landmark == null ||
      address?.landmark == undefined ||
      address?.landmark.trim() == ""
    ) {
      setClicked(true);
      return;
    }

    const addressRequest: AddressRequest = new AddressRequest();
    addressRequest.building = address.building;
    addressRequest.locality = address.locality;
    addressRequest.formattedAddress = address.landmark;
    addressRequest.placeId = address.placeId;
    addressRequest.latitude = address.latitude;
    addressRequest.longitude = address.longitude;
    apiService
      .post(apiService.saveAddress, addressRequest, getToken()!)
      .then((value: unknown) => {
        const responseObject = value as ResponseObject;
        if (responseObject?.status == "success") {
          const savedAddress: Address = responseObject?.object as Address;
          localStorage.setItem("address", btoa(JSON.stringify(savedAddress)));
          handleClose();
          updateAddressList();
        } else if (responseObject?.status == "failed") {
          setAddressLimitFlag(true);
          setAddressLimitMessage(responseObject?.message);
        }
      });
  };

  const handleUpdateAddress = (e: any) => {
    if (
      address?.building == null ||
      address?.building == undefined ||
      address?.building.trim() == ""
    ) {
      setClicked(true);
      return;
    } else if (
      address?.locality == null ||
      address?.locality == undefined ||
      address?.locality.trim() == ""
    ) {
      setClicked(true);
      return;
    }

    const addressRequest: AddressRequest = new AddressRequest();

    addressRequest.building = address.building;
    addressRequest.locality = address.locality;
    addressRequest.formattedAddress = address.landmark;
    addressRequest.placeId = addressForSave.placeId;
    addressRequest.latitude = addressForSave.latitude;
    addressRequest.longitude = addressForSave.longitude;
    apiService
      .put(apiService.updateCustomerAddresses, addressRequest, getToken()!)
      .then((value: unknown) => {
        const responseObject = value as ResponseObject;
        if (responseObject?.status == "success") {
          handleClose();
          updateAddressList();
        }
      });
  };

  const getPlacePredictionsAsync = async (input: string) => {
    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        autocompleteService.current?.getPlacePredictions(
          { input, componentRestrictions: { country: ["IN", "NP"] } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              resolve(predictions || []);
            } else {
              reject(new Error("Failed to fetch predictions"));
            }
          }
        );
      }
    );
  };

  const handleInputChange = async (
    event: React.ChangeEvent<{}>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    try {
      if (autocompleteService.current && newInputValue) {
        const predictions = await getPlacePredictionsAsync(newInputValue);
        setPredictions(predictions);
      }
    } catch (error) {
      console.error(error);
      setPredictions([]);
    }
  };

  const handlePlaceChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    if (!newValue || !newValue?.place_id) return;

    const placesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );

    placesService?.getDetails(
      { placeId: newValue?.place_id },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (placeDetails) {
          setAddress((prevState: any) => ({
            ...prevState,
            landmark: placeDetails?.formatted_address,
            placeId: newValue?.place_id,
            latitude: placeDetails?.geometry?.location?.lat(),
            longitude: placeDetails?.geometry?.location?.lng(),
          }));
        }
      }
    );
  };

  const CustomPopper = useCallback((props: any) => {
    return (
      <Popper
        {...props}
        placement="bottom-start"
        style={{
          ...props.style,
          zIndex: 1600,
          width: "min(100%, calc(100vw - 32px))",
          maxWidth: "min(100%, 400px)",
        }}
      >
        {props.children}
      </Popper>
    );
  }, []);

  const fieldOutlineSx = {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${BRAND} !important`,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background: "rgba(255,255,255,0.95)",
    },
  };

  const showBuildingError =
    clicked && !String(address?.building ?? "").trim();
  const showLocalityError =
    clicked && !String(address?.locality ?? "").trim();
  const showLandmarkError =
    clicked && !String(address?.landmark ?? "").trim();

  return (
    <Dialog
      open={addressOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="address-dialog-title"
      sx={{
        "& .MuiDialog-paper": {
          display: "flex",
          flexDirection: "column",
          borderRadius: isMobile ? 0 : "24px",
          overflow: "hidden",
          maxHeight: isMobile ? "100dvh" : "min(92dvh, 720px)",
          boxShadow: isMobile ? "none" : "0 30px 80px rgba(11, 26, 19, 0.18)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,251,248,0.96) 100%)",
          margin: isMobile ? 0 : "24px auto",
          paddingTop: isMobile ? "env(safe-area-inset-top, 0px)" : 0,
        },
        "& .MuiBackdrop-root": {
          background: "rgba(11, 26, 19, 0.34)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <DialogTitle
        id="address-dialog-title"
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          borderBottom: `1px solid ${alpha(BRAND, 0.1)}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontFamily: "var(--salontym-font-display)",
              fontSize: { xs: "1.45rem", sm: "1.85rem" },
              lineHeight: 1.15,
              color: "#102117",
              fontWeight: 700,
            }}
          >
            {updateAddressFlag ? "Update address" : "Add address"}
          </Typography>
          <Typography
            component="div"
            sx={{
              mt: 0.75,
              color: "#5e6d63",
              fontSize: "0.9rem",
              lineHeight: 1.55,
              maxWidth: "32rem",
            }}
          >
            Search your area, then add flat and locality so we can reach you
            without calls.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          size="small"
          sx={{
            color: BRAND,
            bgcolor: alpha(BRAND, 0.08),
            "&:hover": { bgcolor: alpha(BRAND, 0.15) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          px: { xs: 2, sm: 2.5 },
          py: 2,
          pb: { xs: "max(1.25rem, env(safe-area-inset-bottom))", sm: 2.5 },
        }}
      >
        <Stack spacing={2.25}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: alpha("#102117", 0.55),
                mb: 1,
              }}
            >
              Address details
            </Typography>
            <Stack spacing={1.25}>
              <Input
                autoFocus
                required
                margin="none"
                type="text"
                label="Flat / house / building no."
                value={address?.building}
                name="building"
                onChange={handleAddress}
                fullWidth
                error={showBuildingError}
                sx={{ ...fieldOutlineSx }}
              />
              {showBuildingError ? (
                <Typography component="p" role="alert" variant="caption" sx={{ color: "error.main", mt: -0.5 }}>
                  This field is required
                </Typography>
              ) : null}

              <Input
                required
                margin="none"
                type="text"
                label="Locality / street / sector"
                value={address?.locality}
                name="locality"
                onChange={handleAddress}
                fullWidth
                error={showLocalityError}
                sx={{ ...fieldOutlineSx }}
              />
              {showLocalityError ? (
                <Typography component="p" role="alert" variant="caption" sx={{ color: "error.main", mt: -0.5 }}>
                  This field is required
                </Typography>
              ) : null}
            </Stack>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: alpha("#102117", 0.55),
                mb: 1,
              }}
            >
              Search & select on map
            </Typography>
            {isLoaded ? (
              <Autocomplete
                ListboxProps={{
                  style: { maxHeight: 240, overflow: "auto" },
                }}
                freeSolo
                inputValue={inputValue}
                options={predictions}
                onInputChange={handleInputChange}
                getOptionLabel={(option: any) => option.description ?? ""}
                onChange={handlePlaceChange}
                PaperComponent={(props) => <AutocompleteListPaper {...props} />}
                PopperComponent={CustomPopper}
                renderOption={(props: any, option: any) => {
                  const { key, ...restProps } = props;
                  return (
                    <Box
                      key={option?.place_id}
                      component="li"
                      {...restProps}
                      sx={{
                        flexDirection: "column",
                        width: "100%",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ textAlign: "left", width: "100%" }}>
                        {option?.description ?? ""}
                      </Typography>
                    </Box>
                  );
                }}
                sx={{
                  width: "100%",
                  ...fieldOutlineSx,
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search area, landmark, or pin code"
                    placeholder="e.g. Gaur City, Greater Noida"
                  />
                )}
              />
            ) : (
              <Typography sx={{ fontSize: "0.85rem", color: "#5e6d63" }}>
                Loading places search…
              </Typography>
            )}
            {showLandmarkError ? (
              <Typography component="p" role="alert" variant="caption" sx={{ color: "error.main", mt: 1 }}>
                Select a place from search to set your map location
              </Typography>
            ) : null}
          </Box>

          {addressLimitFlag && addressLimitMessage ? (
            <Typography
              role="alert"
              sx={{
                color: "error.main",
                textAlign: "center",
                fontSize: "0.9rem",
              }}
            >
              {addressLimitMessage}
            </Typography>
          ) : null}

          <Box
            sx={{
              pt: 0.5,
              borderTop: `1px solid ${alpha(BRAND, 0.1)}`,
            }}
          >
            {updateAddressFlag ? (
              <Button
                variant="contained"
                disableElevation
                fullWidth
                sx={{
                  py: 1.35,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "1rem",
                  background: `linear-gradient(135deg, ${BRAND} 0%, #123524 100%)`,
                  boxShadow: `0 12px 28px ${alpha(BRAND, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, #1a6b45 0%, #0f2a1c 100%)`,
                  },
                }}
                onClick={handleUpdateAddress}
              >
                Save changes
              </Button>
            ) : (
              <Button
                variant="contained"
                disableElevation
                fullWidth
                sx={{
                  py: 1.35,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "1rem",
                  background: `linear-gradient(135deg, ${BRAND} 0%, #123524 100%)`,
                  boxShadow: `0 12px 28px ${alpha(BRAND, 0.35)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, #1a6b45 0%, #0f2a1c 100%)`,
                  },
                }}
                onClick={handleSubmit}
              >
                Save address
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
